import React from "react";
import LoadingFeedbackContext from './LoadingFeedbackContext';
/**
 * The react hook for showing/hiding loading feedback overlay
 *
 * @example Basic usage
 * ```tsx
 * const loading = useLoadingFeedback();
 *
 * loading.show({ message: 'Processing...' });
 * // ... do work
 * loading.hide();
 * ```
 *
 * @example With progress
 * ```tsx
 * const loading = useLoadingFeedback();
 *
 * loading.show({ message: 'Uploading...', progress: 0 });
 * // ... update progress
 * loading.update({ progress: 50 });
 * // ... complete
 * loading.hide();
 * ```
 *
 * @example With async/await
 * ```tsx
 * const loading = useLoadingFeedback();
 *
 * const handleSave = async () => {
 *     loading.show({ message: 'Saving...' });
 *     try {
 *         await saveData();
 *     } finally {
 *         loading.hide();
 *     }
 * }
 * ```
 *
 * @export
 */
const useLoadingFeedback = () => {
    const context = React.useContext(LoadingFeedbackContext);
    if (!context) {
        console.warn('useLoadingFeedback must be used within LoadingFeedbackProvider');
        return {
            show: () => { },
            hide: () => { },
            update: () => { },
        };
    }
    return {
        show: context.show,
        hide: context.hide,
        update: context.update,
    };
};
export default useLoadingFeedback;
